import { useEffect, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';

import PersonalDetailsCheck from './PersonalDetailsCheck';
import { useOffersUK } from '../../../../hooks/useOffers/useOffersUK';
import {
  useGetDirectorQuery,
  useGetSelectStateQuery,
} from '../../../../lendingApi/lendingApiQueries';
import ApplicationLoader from '../../../ApplicationLoader/ApplicationLoader';
import { normaliseStepData } from '../../../helpers/stateNormalisation';
import { APPLICANT_STEP_ID } from '../../../helpers/steps';

const PersonalDetailsCheckWithOffersCheck = () => {
  const [dataRequirementErrors, setDataRequirementErrors] =
    useState<ReturnType<typeof useOffersUK>['dataRequirementErrors']>();

  const offersInformation = useOffersUK({ enabled: true });
  const { normalisedState } = useGetSelectStateQuery(
    (data) => normaliseStepData(data)[APPLICANT_STEP_ID],
  );

  const { directors } = useGetDirectorQuery(normalisedState.companyNumber);

  const findMatchingDirector = () => {
    const directorMatchedWithMiddleName = directors?.find((director) => {
      const directorFirstAndMiddleNames = [
        director.first_name,
        director.middle_names,
      ]
        .filter(Boolean)
        .join(' ');

      return (
        directorFirstAndMiddleNames === normalisedState.firstName &&
        director.last_name === normalisedState.lastName
      );
    });

    if (!directorMatchedWithMiddleName) {
      tracking.ampli.lendingDecisionErrored({
        'Error type':
          'State middle name does not match Companies House middle name',
      });
    }

    return directorMatchedWithMiddleName;
  };

  useEffect(() => {
    if (offersInformation.offers) {
      setDataRequirementErrors([
        ...(offersInformation.dataRequirementErrors || []),
        ...getExtraDataRequirementErrors(),
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offersInformation.offers]);

  const getExtraDataRequirementErrors = () => {
    const matchingDirector = findMatchingDirector();

    if (!matchingDirector) {
      tracking.ampli.lendingDecisionErrored({
        'Error type': 'State director does not match Companies House director',
      });

      return ['check_directors_match_companies_house'];
    }

    const stateDOBMatchesCompaniesHouseDOB =
      matchingDirector.month_of_birth === Number(normalisedState.dobMonth) &&
      matchingDirector.year_of_birth === Number(normalisedState.dobYear);

    if (!stateDOBMatchesCompaniesHouseDOB) {
      tracking.ampli.lendingDecisionErrored({
        'Error type': 'State DOB does not match Companies House DOB',
      });

      return ['check_directors_match_companies_house'];
    }

    return [];
  };

  return !dataRequirementErrors ? (
    <ApplicationLoader />
  ) : (
    <PersonalDetailsCheck
      dataRequirementErrors={dataRequirementErrors}
      hasDocumentRequirements={offersInformation.hasDocumentRequirements}
      isRefetchingOffers={offersInformation.isRefetchingOffers}
      includeMiddleName={true}
    />
  );
};

export default PersonalDetailsCheckWithOffersCheck;
